/**
 * One owner-facing right tool rail and drawer (#378 / #401).
 *
 * The rail is permanent. "Hidden" means only the drawer body is collapsed;
 * the square tool buttons never disappear. Expanded labels are optional and
 * reserved via --inspector-rail-width so page content does not overlap.
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactElement, type ReactNode } from 'react';
import {
  INSPECTOR_TABS, activateInspectorTool, clampInspectorWidth, effectiveInspectorMode, inspectorRailWidthPx, safePreviewUrl,
  type InspectorMode, type InspectorSelection, type InspectorTab,
} from '../lib/inspector';
import type { IdeLaunchDecision } from '@brunel/shared';
import { HandoffView } from './HandoffView';
import { ChangesView } from './ChangesView';
import { IdeLaunchControl } from './IdeLaunchControl';
import { NavIcon, TOOL_ICON } from './NavIcons';
import '../inspector.css';

export function InspectorWorkbench({
  mode, widthPx, tab, selection, viewportPx, onMode, onWidth, onTab,
  railLabels = false, onRailLabels,
  notes, roadmap, workBoard, usage, systems, machines, results, ideDecision, ideBusy, ideError, ideOk, onOpenIde,
  observer, badges,
}: {
  readonly mode: InspectorMode;
  readonly widthPx: number;
  readonly tab: InspectorTab;
  readonly selection: InspectorSelection;
  readonly viewportPx: number;
  readonly onMode: (mode: InspectorMode) => void;
  readonly onWidth: (px: number) => void;
  readonly onTab: (tab: InspectorTab) => void;
  readonly railLabels?: boolean;
  readonly onRailLabels?: (show: boolean) => void;
  readonly notes?: ReactNode;
  readonly roadmap?: ReactNode;
  readonly workBoard?: ReactNode;
  readonly usage?: ReactNode;
  readonly systems?: ReactNode;
  readonly machines?: ReactNode;
  readonly results?: ReactNode;
  readonly ideDecision?: IdeLaunchDecision;
  readonly ideBusy?: boolean;
  readonly ideError?: string | null;
  readonly ideOk?: string | null;
  readonly onOpenIde?: () => void;
  readonly observer?: ReactNode;
  readonly badges?: Partial<Readonly<Record<InspectorTab, number>>>;
}) {
  const visibleMode = effectiveInspectorMode(mode, viewportPx);
  const open = visibleMode !== 'hidden';
  const railWidth = inspectorRailWidthPx(railLabels);
  const width = clampInspectorWidth(widthPx, viewportPx, railWidth);
  const contextLabel = selection.workerLabel ?? selection.taskTitle
    ?? (selection.workerId != null ? selection.workerId : 'No Worker or task selected');

  const openTool = (next: InspectorTab) => {
    const nextState = activateInspectorTool(visibleMode, tab, next);
    if (nextState.tab !== tab) onTab(nextState.tab);
    onMode(nextState.mode);
  };

  return (
    <aside className={`inspector-shell${open ? ' drawer-open' : ' rail-only'}${railLabels ? ' rail-labels' : ''}`}
      style={{ '--inspector-rail-width': `${railWidth}px` } as CSSProperties}
      aria-label="Right drawer tools" data-mode={visibleMode} data-tab={tab}
      data-rail-labels={railLabels ? 'true' : 'false'}>
      {open && (
        <section id="right-drawer-panel" className={`inspector-workbench mode-${visibleMode}`}
          style={{ '--inspector-width': `${width}px` } as CSSProperties}
          aria-label={`${tabLabel(tab)} drawer`}>
          <ResizeHandle width={width} viewportPx={viewportPx} railWidthPx={railWidth} onWidth={onWidth} />
          <header className="inspector-head">
            <div className="row spread wrap">
              <div className="stack tight">
                <h2>{tabLabel(tab)}</h2>
                <small className="muted inspector-context" title={contextLabel}>{contextLabel}</small>
              </div>
              <div className="row inspector-head-actions">
                {ideDecision && onOpenIde && (
                  <IdeLaunchControl decision={ideDecision} busy={ideBusy === true}
                    error={ideError ?? null} ok={ideOk ?? null} onOpen={onOpenIde} />
                )}
                <button type="button" className="ghost icon inspector-collapse"
                  aria-label="Collapse right drawer" title="Collapse right drawer"
                  onClick={() => onMode('hidden')}>
                  <ChevronRightIcon />
                </button>
              </div>
            </div>
          </header>

          <div className="inspector-body" role="tabpanel" aria-label={tabLabel(tab)}>
            {tab === 'observer' && (observer ?? <PlaceholderTab title="Observer" blurb="UX, Core and owner discussion appears here." />)}
            {tab === 'notes' && (notes ?? <PlaceholderTab title="Notes" blurb="Private developer note pages appear here." />)}
            {tab === 'workboard' && (workBoard ?? <PlaceholderTab title="Work Board" blurb="Read-only Brunel work state appears here." />)}
            {tab === 'roadmap' && (roadmap ?? <PlaceholderTab title="Roadmap" blurb="Roadmap items from the shared store appear here." />)}
            {tab === 'results' && (results ?? <PlaceholderTab title="Results" blurb="Recently finished work appears here." />)}
            {tab === 'usage' && (usage ?? <PlaceholderTab title="AI Usage" blurb="Provider usage readings appear here when available." />)}
            {tab === 'systems' && (systems ?? <PlaceholderTab title="Systems" blurb="Read-only service health appears here when supported." />)}
            {tab === 'machines' && (machines ?? <PlaceholderTab title="Machine Capacity" blurb="Pick a computer in the Machines tab to see what it is under and the limits on it." />)}
            {tab === 'work_result' && (
              <ChangesView workerLabel={selection.workerLabel} projectName={selection.projectName}
                taskTitle={selection.taskTitle} taskId={selection.taskId} nodeName={selection.nodeName}
                sessionId={selection.sessionId} branch={selection.branch} filesChanged={selection.filesChanged}
                patchText={selection.patchText} runSummary={selection.runSummary} handoff={selection.handoff} />
            )}
            {tab === 'handoff' && <HandoffView handoff={selection.handoff} />}
            {tab === 'preview' && <PreviewTab previewUrl={selection.previewUrl} />}
          </div>
        </section>
      )}

      <nav className="inspector-tool-rail" role="tablist" aria-label="Right drawer tools">
        {INSPECTOR_TABS.map((item) => {
          const selected = open && tab === item.id;
          const count = badges?.[item.id] ?? 0;
          const name = item.label;
          return (
            <button key={item.id} type="button" role="tab"
              className={selected ? 'inspector-rail-button on' : 'inspector-rail-button'}
              aria-selected={selected} aria-label={name} title={name}
              aria-controls="right-drawer-panel" onClick={() => openTool(item.id)}>
              <ToolIcon tool={item.id} />
              {railLabels && <span className="inspector-rail-label">{name}</span>}
              {count > 0 && <span className="inspector-rail-count" aria-hidden="true">{Math.min(count, 99)}</span>}
            </button>
          );
        })}
        {onRailLabels && (
          <button type="button" className="inspector-rail-labels-toggle"
            aria-pressed={railLabels}
            aria-label={railLabels ? 'Hide labels' : 'Show labels'}
            title={railLabels ? 'Hide labels' : 'Show labels'}
            onClick={() => onRailLabels(!railLabels)}>
            {railLabels ? 'Hide labels' : 'Show labels'}
          </button>
        )}
      </nav>
    </aside>
  );
}

function tabLabel(tab: InspectorTab): string {
  return INSPECTOR_TABS.find((item) => item.id === tab)?.label ?? tab;
}

function PlaceholderTab({ title, blurb }: { readonly title: string; readonly blurb: string }) {
  return <div className="inspector-empty" role="status"><p>{title}</p><p className="muted">{blurb}</p></div>;
}

function PreviewTab({ previewUrl }: { readonly previewUrl: string | null }) {
  const url = safePreviewUrl(previewUrl);
  const [frameKey, setFrameKey] = useState(0);
  if (url == null) return (
    <div className="inspector-empty" role="status"><p>No preview URL</p>
      <p className="muted">When a Worker supplies a preview link, it opens here. Brunel does not invent one.</p></div>
  );
  return (
    <div className="inspector-preview stack">
      <div className="row spread wrap">
        <a className="link" href={url} target="_blank" rel="noreferrer noopener">Open preview externally</a>
        <button type="button" className="ghost" onClick={() => setFrameKey((n) => n + 1)}>Refresh</button>
      </div>
      <p className="muted inspector-preview-url" title={url}>{url}</p>
      <iframe key={frameKey} className="inspector-preview-frame" title="Preview" src={url}
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups" referrerPolicy="no-referrer" />
    </div>
  );
}

function ResizeHandle({ width, viewportPx, railWidthPx, onWidth }: {
  readonly width: number;
  readonly viewportPx: number;
  readonly railWidthPx: number;
  readonly onWidth: (px: number) => void;
}) {
  const dragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(width);
  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    dragging.current = true; startX.current = event.clientX; startWidth.current = width;
    event.currentTarget.setPointerCapture(event.pointerId);
  }, [width]);
  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    onWidth(clampInspectorWidth(startWidth.current + startX.current - event.clientX, viewportPx, railWidthPx));
  }, [onWidth, viewportPx, railWidthPx]);
  const onPointerUp = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    dragging.current = false;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  }, []);
  useEffect(() => { startWidth.current = width; }, [width]);
  return <div className="inspector-resize" role="separator" aria-orientation="vertical"
    aria-label="Resize right drawer" aria-valuenow={width}
    onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp} />;
}

function ToolIcon({ tool }: { readonly tool: InspectorTab }): ReactElement {
  const shared = TOOL_ICON[tool];
  if (shared !== undefined) return <NavIcon name={shared} strokeWidth={1.9} size={20} />;
  const common = { viewBox:'0 0 24 24', width:20, height:20, fill:'none', stroke:'currentColor',
    strokeWidth:1.9, strokeLinecap:'round' as const, strokeLinejoin:'round' as const,
    'aria-hidden':true, focusable:false };
  switch (tool) {
    case 'observer': return <svg {...common}><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" /><circle cx="12" cy="12" r="2.4" /></svg>;
    case 'results': return <svg {...common}><path d="M4 19V5" /><path d="M4 19h16" /><path d="M8 15v-4M12 15V8M16 15v-6" /></svg>;
    case 'usage': return <svg {...common}><path d="M4 17a8 8 0 0 1 16 0" /><path d="m12 13 4-4" /><path d="M7 18h10" /></svg>;
    case 'systems': return <svg {...common}><rect x="3" y="4" width="18" height="6" rx="1.5" /><rect x="3" y="14" width="18" height="6" rx="1.5" /><path d="M7 7h.01M7 17h.01M11 7h7M11 17h7" /></svg>;
    case 'work_result': return <svg {...common}><path d="M6 3h9l3 3v15H6z" /><path d="M14 3v4h4" /><path d="m9 14 2 2 4-5" /></svg>;
    case 'handoff': return <svg {...common}><path d="M4 8h12" /><path d="m13 5 3 3-3 3" /><path d="M20 16H8" /><path d="m11 13-3 3 3 3" /></svg>;
    case 'preview': return <svg {...common}><rect x="3" y="4" width="18" height="13" rx="2" /><path d="M8 21h8M12 17v4" /></svg>;
    default: return <svg {...common}><circle cx="12" cy="12" r="7" /></svg>;
  }
}
function ChevronRightIcon(): ReactElement {
  return <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"><path d="m9 6 6 6-6 6" /></svg>;
}
